/**
 * The customer map's search box.
 *
 * It is the site grid's search field, not a lookalike: the same
 * `ToolbarSearchInput`, the same "Search {{structures}}" placeholder, and the
 * same in-field count of what survived, so an operator who searches a list of
 * structures searches the map the same way and reads the same answer.
 *
 * Typed text is held locally and committed on a debounce, because the committed
 * value is the map's URL — one entry per word rather than one per keystroke.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ToolbarSearchInput } from '@sophocles/data-grids/components/toolbar';
import useDebounce from '@sophocles/hooks/useDebounce';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';

// The grid toolbar's own quick-filter debounce, so both fields feel alike.
const SEARCH_DEBOUNCE_MS = 300;

/**
 * @param {object} props
 * @param {string} props.value - the committed search text (the map URL's `q`).
 * @param {(text: string) => void} props.onChange - commit a new search text.
 * @param {number} props.matchCount - rows the current search and filters leave.
 */
export default function MapSearchField({ value, onChange, matchCount }) {
  const { t } = useTranslation();
  const siteTerm = useSiteTerm();

  const [text, setText] = useState(value);
  const debouncedText = useDebounce(text, SEARCH_DEBOUNCE_MS);
  const committedRef = useRef(value);
  committedRef.current = value;

  // Committing is a reaction to the text settling and nothing else. Watching
  // the handler too would re-fire this with the old text whenever the URL
  // moves — which is exactly what "clear all" does, and the stale commit would
  // put the search straight back.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (debouncedText !== committedRef.current) onChangeRef.current(debouncedText);
  }, [debouncedText]);

  // A reset from elsewhere (clear all, a fresh link) owns the box again.
  useEffect(() => setText(value), [value]);

  const placeholder = t('sites.searchSites', 'Search {{term}}', { term: siteTerm.plural });

  return (
    <ToolbarSearchInput
      value={text}
      onChange={setText}
      placeholder={placeholder}
      inputProps={{ 'aria-label': placeholder }}
      itemCount={matchCount}
      countNoun={siteTerm.plural}
    />
  );
}
